import { Capacitor } from '@capacitor/core';
import type { WaypointInput } from '../types/jobs';
import { keepFix, planCadence, type CadencePlan, type KeptFix } from '../utils/gpsCadence';

type LocationCallback = (wp: WaypointInput) => void;

// Which fixes are kept is decided by the cadence plan (utils/gpsCadence):
// by distance travelled and turns, bounded by time, so a fast mower records
// often and a pruning crew rarely. Both jobs and sprays use it.
const DEFAULT_PLAN: CadencePlan = planCadence('job');

/**
 * Native and browser APIs return values like `NaN`, `-1`, or `Infinity` for
 * fields the device can't produce (heading when stationary, altitude without a
 * barometer, etc.). The backend rejects these via Joi `number()` validation —
 * which used to cause every upload batch to 400 silently and points to stack
 * up in IndexedDB with a healthy-looking WiFi connection.
 */
function cleanNumber(v: number | null | undefined): number | null {
  if (v == null) return null;
  if (typeof v !== 'number') return null;
  if (!Number.isFinite(v)) return null;
  return v;
}

/**
 * Unified background location service.
 *
 * Native (Capacitor/Android): uses @capgo/background-geolocation with a
 * foreground service so GPS continues when the app is backgrounded.
 *
 * Web: falls back to navigator.geolocation.watchPosition for dev/test.
 */
class BackgroundLocationService {
  private callbacks: Set<LocationCallback> = new Set();
  private running = false;
  private webWatchId: number | null = null;
  private lastKept: KeptFix | null = null;
  private plan: CadencePlan = DEFAULT_PLAN;

  /**
   * Start tracking. Only one session at a time. The plan is set on every
   * start rather than remembered, so a job tracked after a spray gets its own
   * cadence without anyone having to reset it.
   */
  async start(plan: CadencePlan = DEFAULT_PLAN): Promise<void> {
    if (this.running) return;
    this.running = true;
    this.lastKept = null;
    this.plan = plan;

    if (Capacitor.isNativePlatform()) {
      await this.startNative();
    } else {
      this.startWeb();
    }
  }

  /** Stop tracking and clean up. */
  async stop(): Promise<void> {
    if (!this.running) return;
    this.running = false;

    if (Capacitor.isNativePlatform()) {
      await this.stopNative();
    } else {
      this.stopWeb();
    }
  }

  /**
   * Force-stop the platform GPS / foreground-service notification regardless
   * of this service's `running` flag.
   *
   * Why this exists: the Capgo foreground service runs in its own native
   * process. After a full app cold-start the JS-side `running` flag resets
   * to `false`, so a normal `stop()` short-circuits — but the OS-level
   * service from the previous session may still be alive, with its
   * notification pinned in the shade. Calling the plugin's `stop()`
   * unconditionally clears that orphan notification.
   *
   * Safe to call when nothing is running: the plugin treats it as a no-op.
   */
  async forceStopNative(): Promise<void> {
    this.running = false;
    if (Capacitor.isNativePlatform()) {
      try {
        await this.stopNative();
      } catch (e) {
        console.warn('[BackgroundLocation] forceStopNative failed', e);
      }
    } else {
      this.stopWeb();
    }
  }

  onLocation(cb: LocationCallback): void {
    this.callbacks.add(cb);
  }

  offLocation(cb: LocationCallback): void {
    this.callbacks.delete(cb);
  }

  isRunning(): boolean {
    return this.running;
  }

  // ---------------------------------------------------------------------------
  // Native (Capgo plugin)
  // ---------------------------------------------------------------------------

  private async startNative(): Promise<void> {
    const { BackgroundGeolocation } = await import('@capgo/background-geolocation');

    await BackgroundGeolocation.start(
      {
        backgroundTitle: 'FactreeTech GPS',
        backgroundMessage: 'Tracking in progress',
        distanceFilter: this.plan.distanceFilterM,
        requestPermissions: true,
        stale: false,
      },
      (location, error) => {
        if (error) {
          console.warn('[BackgroundLocation] error:', error);
          return;
        }
        if (!location) return;

        const wp: WaypointInput = {
          lat: location.latitude,
          lng: location.longitude,
          accuracy: cleanNumber(location.accuracy),
          altitude: cleanNumber(location.altitude),
          heading: cleanNumber(location.bearing),
          speed: cleanNumber(location.speed),
          recorded_at: location.time ? new Date(location.time).toISOString() : new Date().toISOString(),
        };
        this.emit(wp);
      },
    );
  }

  private async stopNative(): Promise<void> {
    const { BackgroundGeolocation } = await import('@capgo/background-geolocation');
    await BackgroundGeolocation.stop();
  }

  // ---------------------------------------------------------------------------
  // Web fallback (browser Geolocation API)
  // ---------------------------------------------------------------------------

  private startWeb(): void {
    if (!navigator.geolocation) {
      console.warn('[BackgroundLocation] Geolocation API not available');
      return;
    }

    this.webWatchId = navigator.geolocation.watchPosition(
      (pos) => {
        const wp: WaypointInput = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: cleanNumber(pos.coords.accuracy),
          altitude: cleanNumber(pos.coords.altitude),
          heading: cleanNumber(pos.coords.heading),
          speed: cleanNumber(pos.coords.speed),
          recorded_at: new Date().toISOString(),
        };
        this.emit(wp);
      },
      (err) => console.warn('[BackgroundLocation] web error:', err.message),
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 5_000 },
    );
  }

  private stopWeb(): void {
    if (this.webWatchId !== null) {
      navigator.geolocation.clearWatch(this.webWatchId);
      this.webWatchId = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Internal
  // ---------------------------------------------------------------------------

  private emit(wp: WaypointInput): void {
    const kept = keepFix(this.plan, this.lastKept, {
      lat: wp.lat,
      lng: wp.lng,
      at: Date.now(),
      speed: wp.speed,
      heading: wp.heading,
    });
    if (!kept) return;
    this.lastKept = kept;
    for (const cb of this.callbacks) {
      try {
        cb(wp);
      } catch (e) {
        console.error('[BackgroundLocation] callback error:', e);
      }
    }
  }
}

const backgroundLocation = new BackgroundLocationService();
export default backgroundLocation;
